import { supabase } from "@/integrations/supabase/client";
import { VoiceSearchResponse, VoiceSearchResult } from "@/types/agent/agent";
import { Voice } from "@/types/voice/voice";

// Simple cache for voice search results
interface CachedSearchResult {
  voices: VoiceSearchResult[];
  timestamp: number;
  expiresAt: number;
}

export class VoiceSearchService {
  private static readonly CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes
  private static searchCache = new Map<string, CachedSearchResult>();

  public extractVoiceSearchTerms(voicePreference: string): string[] {
    // Clean and split the preference into meaningful terms
    const terms = voicePreference
      .toLowerCase()
      .replace(/[^\w\s-]/g, "") // Remove punctuation except hyphens
      .split(/\s+/)
      .filter(
        (term) =>
          term.length > 2 && // Filter out short words
          !["and", "or", "but", "with", "very", "quite", "that", "this", "the", "a", "an"].includes(term),
      );

    return terms.length > 0 ? terms : [voicePreference.toLowerCase()];
  }

  /**
   * Search for optimal voice based on terms.
   * If availableVoices provided, uses client-side search on those voices (faster, no API call).
   * Otherwise, calls the edge function for fresh ElevenLabs data.
   */
  public async searchOptimalVoice(searchTerms: string[], availableVoices?: Voice[]): Promise<VoiceSearchResult | null> {
    try {
      let searchResults: VoiceSearchResult[] = [];

      // If we have available voices, perform client-side search first
      if (availableVoices && availableVoices.length > 0) {
        console.log("Performing client-side voice search with", availableVoices.length, "voices");
        searchResults = this.searchVoicesClientSide(availableVoices, searchTerms);

        if (searchResults.length > 0) {
          console.log(`Found ${searchResults.length} matching voices using client-side search`);
          return searchResults[0];
        }

        console.log("No matches found in client-side search, falling back to edge function");
      }

      // Fallback to edge function search if:
      // 1. No available voices provided, OR
      // 2. Client-side search found no matches
      const cacheKey = this.getCacheKey(searchTerms);
      const cached = this.getFromCache(cacheKey);

      if (cached) {
        console.log("Using cached voice search results for terms:", searchTerms.join(", "));
        searchResults = cached;
      } else {
        console.log("Fetching voice search results from edge function for terms:", searchTerms.join(", "));
        const { data, error } = await supabase.functions.invoke("elevenlabs", {
          body: {
            resource: "voices",
            action: "search",
            params: { terms: searchTerms },
          },
        });

        if (error) {
          console.error("Voice search error:", error);
          return null;
        }

        const response: VoiceSearchResponse = data;
        searchResults = response.voices || [];

        // Cache the results
        if (searchResults.length > 0) {
          this.setInCache(cacheKey, searchResults);
        }
      }

      if (searchResults.length === 0) {
        return null;
      }

      // If availableVoices provided, validate that the top result exists
      if (availableVoices && availableVoices.length > 0) {
        const topResult = searchResults[0];
        const voiceExists = availableVoices.some((v) => v.id === topResult.voice_id);

        if (!voiceExists) {
          console.warn(`Top search result (${topResult.voice_id}) not found in available voices. ` + `Searching alternatives...`);
          // Try to find any matching voice in available voices
          for (const result of searchResults) {
            if (availableVoices.some((v) => v.id === result.voice_id)) {
              console.log(`Found alternative voice: ${result.voice_id}`);
              return result;
            }
          }
          console.warn("No search results found in available voices");
          return null;
        }
      }

      return searchResults[0];
    } catch (error) {
      console.error("Voice search failed:", error);
      return null;
    }
  }

  /**
   * Search for the best matching voice from a given list, with no external API calls.
   * Returns null if no voice scores above zero.
   */
  public searchVoicesFromList(voices: Voice[], searchTerms: string[]): VoiceSearchResult | null {
    const results = this.searchVoicesClientSide(voices, searchTerms);
    return results.length > 0 ? results[0] : null;
  }

  /**
   * Client-side voice search - faster alternative to edge function when voices are already loaded
   */
  private searchVoicesClientSide(voices: Voice[], searchTerms: string[]): VoiceSearchResult[] {
    const voiceScores = new Map<string, { voice: Voice; score: number; matchedTerms: string[] }>();

    // Score each voice based on match with search terms
    for (const voice of voices) {
      let score = 0;
      const matchedTerms: string[] = [];

      for (const term of searchTerms) {
        const termScore = this.scoreVoiceForTerm(voice, term);
        if (termScore > 0) {
          score += termScore;
          matchedTerms.push(term);
        }
      }

      if (score > 0) {
        voiceScores.set(voice.id, {
          voice,
          score,
          matchedTerms,
        });
      }
    }

    // Convert to results array and sort by score
    const results: VoiceSearchResult[] = Array.from(voiceScores.values())
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map(({ voice, score, matchedTerms }) => ({
        voice_id: voice.id,
        name: voice.name,
        category: voice.category || "general",
        description: voice.description,
        labels: voice.labels,
        matchCount: score,
        matchedTerms,
      }));

    return results;
  }

  /**
   * Score a voice for a specific search term
   */
  private scoreVoiceForTerm(voice: Voice, searchTerm: string): number {
    const term = searchTerm.toLowerCase();
    let score = 0;

    // Check for contradictory gender terms
    if ((term === "female" && voice.labels?.gender === "male") || (term === "male" && voice.labels?.gender === "female")) {
      return 0;
    }

    // Voice name (highest priority - 10 points for exact, 5 for partial)
    if (voice.name) {
      const name = voice.name.toLowerCase();
      if (name === term) {
        score += 10;
      } else if (name.includes(term)) {
        score += 5;
      }
    }

    // Gender
    if (voice.labels?.gender) {
      const gender = voice.labels.gender.toLowerCase();
      if (gender === term) {
        score += 8;
      } else if (gender.includes(term)) {
        score += 4;
      }
    }

    // Accent
    if (voice.labels?.accent) {
      const accent = voice.labels.accent.toLowerCase();
      if (accent === term) {
        score += 8;
      } else if (accent.includes(term)) {
        score += 4;
      }
    }

    // Age
    if (voice.labels?.age) {
      const age = voice.labels.age.toLowerCase();
      if (age === term) {
        score += 6;
      } else if (age.includes(term)) {
        score += 3;
      }
    }

    // Use case
    if (voice.labels?.use_case) {
      const useCase = voice.labels.use_case.toLowerCase();
      if (useCase === term) {
        score += 6;
      } else if (useCase.includes(term)) {
        score += 3;
      }
    }

    // Descriptive labels
    if (voice.labels?.descriptive) {
      const descriptive = voice.labels.descriptive.toLowerCase();
      if (descriptive === term) {
        score += 5;
      } else if (descriptive.includes(term)) {
        score += 2;
      }
    }

    // Category
    if (voice.category) {
      const category = voice.category.toLowerCase();
      if (category === term) {
        score += 4;
      } else if (category.includes(term)) {
        score += 2;
      }
    }

    // Description
    if (voice.description) {
      const description = voice.description.toLowerCase();
      if (description === term) {
        score += 3;
      } else if (description.includes(term)) {
        score += 1;
      }
    }

    // Language
    if (voice.labels?.language) {
      const language = voice.labels.language.toLowerCase();
      if (language === term) {
        score += 3;
      } else if (language.includes(term)) {
        score += 1;
      }
    }

    return score;
  }

  /**
   * Validate that a voice exists in available voices before using it.
   */
  public validateVoiceExists(voiceId: string, availableVoices: Voice[]): boolean {
    return availableVoices.some((v) => v.id === voiceId);
  }

  /**
   * Get a fallback voice if the preferred voice doesn't exist.
   */
  public getFallbackVoice(preferredVoiceId: string, availableVoices: Voice[]): Voice | null {
    // First, try to get a voice from recommended list
    if (availableVoices.length === 0) {
      return null;
    }

    // Return first available voice as fallback
    return availableVoices[0];
  }

  private getCacheKey(terms: string[]): string {
    return `voice_search_${terms.sort().join("_")}`;
  }

  private getFromCache(key: string): VoiceSearchResult[] | null {
    const cached = VoiceSearchService.searchCache.get(key);

    if (!cached) {
      return null;
    }

    // Check if cache has expired
    if (Date.now() > cached.expiresAt) {
      VoiceSearchService.searchCache.delete(key);
      return null;
    }

    return cached.voices;
  }

  private setInCache(key: string, voices: VoiceSearchResult[]): void {
    VoiceSearchService.searchCache.set(key, {
      voices,
      timestamp: Date.now(),
      expiresAt: Date.now() + VoiceSearchService.CACHE_DURATION_MS,
    });

    // Keep cache size reasonable (max 20 entries)
    if (VoiceSearchService.searchCache.size > 20) {
      const firstKey = VoiceSearchService.searchCache.keys().next().value;
      VoiceSearchService.searchCache.delete(firstKey);
    }
  }

  public static clearCache(): void {
    VoiceSearchService.searchCache.clear();
  }
}
